import {
  ProcessCellForExportParams,
  ProcessHeaderForExportParams,
} from '@ag-grid-community/core';
import {
  SignaturePageComponent,
  SignaturePageImageData,
} from 'src/store/signaturePage/types';
import { toPng } from 'html-to-image';
import {
  getImageData,
  getYPosition,
  loadPageImage,
} from 'src/utils/SignatureUtils';
import { signaturePageContentWidth } from 'src/components/Signature/SignaturePageContent';
import { InputType } from 'src/services/api';

export const processContractTableHeaderCallback = (
  params: ProcessHeaderForExportParams,
) => {
  if (params.column.getColId() === 'recipient') {
    // For the 'recipient' column, we need to display three separate columns in the CSV export.
    // This is because the 'recipient' column contains nested information (first name, last name, email)
    // that we want to present as individual columns in the exported CSV.
    return `"Recipient first name","Recipient last name","Recipient email"`;
  }

  return `"${params.column.getColDef().headerName || ''}"`;
};

export const processContractTableCellCallback = (
  params: ProcessCellForExportParams,
) => {
  const rowData = params.node?.data;
  const colId = params.column.getColId();

  // Customize cell values during CSV export based on column ID.
  switch (colId) {
    case 'recipient':
      return `"${rowData.recipient?.givenName || ''}","${
        rowData.recipient?.familyName || ''
      }","${rowData.recipient?.email || ''}"`;

    case 'createdAt':
    case 'submissionDate':
    case 'latestSubmissionDate':
      return rowData[colId]
        ? `${new Date(Date.parse(rowData[colId])).toLocaleDateString()}`
        : '';

    // status column first letter to uppercase
    case 'status': {
      const status = rowData[colId] || '';
      return status.at(0).toUpperCase() + status.slice(1);
    }
    // requestsCount and submissionsCount columns should be 0 if null or undefined in the rowData object
    case 'requestsCount': {
      const requestsCount = rowData[colId];
      return requestsCount;
    }
    case 'submissionsCount': {
      const submissionsCount = rowData[colId];
      return submissionsCount;
    }
    default:
      return `"${params.value || ''}"`;
  }
};

/**
 * Converts a text based signature component into a Base64-encoded image.
 * @returns {string} - A Promise that resolves with the Base64-encoded image.
 */
export function textToBase64Image(pageComponent: SignaturePageComponent) {
  // Return early if the page component has no value, which is likely when an optional component is skipped by the client
  if (!pageComponent.value) return '';

  const elementId = pageComponent.key;
  const textElement = document.getElementById(elementId);
  if (!textElement) return '';

  // The pixel ratio is crucial in determining the size of the generated Base64 image.
  // When the pixel ratio is set to 1, the dimensions of the element and the Base64 image are identical.
  // However, using a pixel ratio of 1 on mobile devices results in an excessively small Base64 image.
  const pixelRatio =
    signaturePageContentWidth /
    Math.min(window.innerWidth, signaturePageContentWidth); // adjust pixel ratio for mobile devices

  return toPng(textElement, {
    pixelRatio,
  });
}

/**
 * Formats the page components to use the y-coordinate
 * relative to the PDF page it is placed on instead of the complete PDF canvas.
 * @param pageImages all the pdf page images
 * @param pageComponents all signature page components
 */
export const getPageComponentsRelativeToPage = async (
  pageImages: SignaturePageImageData[],
  pageComponents: SignaturePageComponent[],
) => {
  const pageImagesWithElement = await Promise.all(
    pageImages.map(async (pageImage) => {
      const pageImageElement = await loadPageImage(pageImage.url);
      const imageData = getImageData(pageImageElement);

      return { ...pageImage, imgElement: pageImageElement, imageData };
    }),
  );

  return pageComponents.map((pageComponent) => {
    // all previous pages
    const allPrevPages = pageImagesWithElement.filter(
      (p) => p.pageNumber < (pageComponent.page || 1),
    );

    const currentPageImageElement = pageImagesWithElement.filter(
      (p) => p.pageNumber === pageComponent.page,
    );

    const { height: pageHeight = 0, width: pageWidth = 0 } =
      currentPageImageElement?.length > 0
        ? currentPageImageElement.at(0)?.imgElement || {}
        : {};

    // sum of heights of all the prev pages
    const prevPagesHeight = allPrevPages.reduce((sum, page) => {
      const { height, width } = page.imgElement;
      const sizeMultiplier = signaturePageContentWidth / width;
      // Calculate page image height with respect to the PDF width.
      return sum + height * sizeMultiplier;
    }, 0);

    return {
      ...pageComponent,
      yPosition: getYPosition(
        prevPagesHeight,
        pageComponent.page || 1,
        pageComponent.yPosition,
        0,
      ),
      // We calculate x,y, width and height relative to the canvas we have created for pdf preview
      // so we also want to save the canvas's width/height of the page on which component is rendered
      // this will help in calculating the scaling factor and render the components on original pdf which
      // doesn't have exact dimensions as the canvas
      pageWidth: signaturePageContentWidth,
      // when rendering the preview we fix width but height we calculate based on actual height of the page image
      // so while sending bounds for the component we need to recalculate that same height
      pageHeight: pageHeight * (signaturePageContentWidth / pageWidth),
    };
  });
};

export const hasSignatureCompnentsWithDuplicateLabels = (
  components: SignaturePageComponent[],
  newComponentLabel: string,
) => components.some((component) => component.label === newComponentLabel);

/**
 * Checks if there are any client component added for the client to sign.
 * @param pageComponents - The array of contract page components.
 * @returns `true` if client components exist, `false` otherwise.
 */
export const hasValidClientContractComponents = (
  pageComponents: SignaturePageComponent[],
) => {
  const clientPageComponents =
    pageComponents?.filter(
      (component) => component.inputType === InputType.Client,
    ) || [];

  return clientPageComponents.length > 0;
};
